/**
 * EmptyState Component
 *
 * Reusable empty-state visual: large disabled-color icon, title,
 * optional description, optional CTA button.
 */
import { Box, Button, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    startIcon?: ReactNode;
  };
  /** Smaller variant for inline lists/cards (icon size 40 instead of 64). */
  compact?: boolean;
}

export function EmptyState({ icon, title, description, action, compact = false }: EmptyStateProps) {
  const iconSize = compact ? 40 : 64;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: compact ? 3 : 6,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize * 1.6,
          height: iconSize * 1.6,
          borderRadius: '50%',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15, 23, 42, 0.04)',
          color: 'text.disabled',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            '& > svg': { fontSize: iconSize },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        variant={compact ? 'subtitle1' : 'h6'}
        component="p"
        fontWeight={600}
        sx={{ mb: description ? 0.5 : 2 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 420, mb: action ? 3 : 0 }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={action.onClick} startIcon={action.startIcon}>
            {action.label}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

export default EmptyState;
